import { TippyCustomzie, useTippyLayout } from "components/TippyCustomzie"
import { ClearAll, FilterIcon } from "components/Filter"
import FormSelectFilter from "pages/conversations/molecules/FormSelectFilter"
import Button from "components/Button/Button"
import { ActionLogQueryState } from "../action-log.type"
import { useState } from "react"
import { cloneDeep } from "lodash"
import { CalendarCustom } from "components/Calendar/Calendar"
import { useBoolean, useWindowSize } from "helpers/hooks"
import { SelectionRangeDetail } from "pages/conversations/conversations.type"
import { formatDate } from "helpers/utils"
import SelectMulti from "components/Select/SelectMulti"
import { endOfDay, startOfDay, subDays } from "date-fns"
import { FilterHeader } from "components/Filter/FilterHeader"
import { FilterCollapse } from "components/Filter/FilterCollapse"

interface Props {
  showFilter: {
    value: boolean
    setValue: (value: boolean) => void
  }
  queryState: ActionLogQueryState
  setQueryState: (newValue: ActionLogQueryState) => void
  projects: {
    id: string
    name: string
    code: string
  }[]
  users: {
    id: string
    fullname: string
    email: string
  }[]
}
export const ActionLogFilterCard = (props: Props) => {
  const { isMobile } = useWindowSize()
  const { TippyLayout } = useTippyLayout()
  const { showFilter, queryState, setQueryState, projects, users } = props
  const orginalStartDate = startOfDay(subDays(new Date(), 6))
  const orginalEndDate = endOfDay(new Date())
  const [filterModel, setFilterModel] = useState<{ [key: string]: string }>({
    "-1": "-1",
  })
  const isCalendarRanges = useBoolean(false)
  const [selectionRange, setSelectionRange] = useState<SelectionRangeDetail>({
    key: "selection",
    startDate: orginalStartDate,
    endDate: orginalEndDate,
  })
  const [startDateCreate, setStartDateCreate] = useState<Date | null>(null)
  const [endDateCreate, setEndDateCreate] = useState<Date | null>(null)
  const [selectedProjects, setSelectedProjects] = useState<
    {
      label: string
      value: string
    }[]
  >([])
  const [selectedUsers, setSelectedUsers] = useState<
    {
      label: string
      value: string
    }[]
  >([])

  const onChangeDate = (ranges) => {
    setSelectionRange(ranges.selection)
  }
  const onClearInputSelectionRanges = () => {
    setStartDateCreate(null)
    setEndDateCreate(null)
    isCalendarRanges.setValue(false)
  }

  const onCancelSelectionRanges = (
    startDate: Date | null,
    endDate: Date | null
  ) => {
    isCalendarRanges.setValue(false)
    setSelectionRange({
      key: "selection",
      startDate: startDate ? new Date(startDate) : orginalStartDate,
      endDate: endDate ? new Date(endDate) : orginalEndDate,
    })
  }
  const onChangeModalSelectRanges = (newValue: boolean) => () => {
    isCalendarRanges.setValue(newValue)
    setSelectionRange({
      key: "selection",
      startDate: startDateCreate ? new Date(startDateCreate) : orginalStartDate,
      endDate: endDateCreate ? new Date(endDateCreate) : orginalEndDate,
    })
  }
  const onApplySelectionRanges = () => {
    setStartDateCreate(new Date(selectionRange.startDate))
    setEndDateCreate(new Date(selectionRange.endDate))
    isCalendarRanges.setValue(false)
  }

  const onCheckActiveFilter = () => {
    return (
      (Object.keys(filterModel).length > 0 && !filterModel["-1"]) ||
      (startDateCreate && endDateCreate ? true : false) ||
      Boolean(selectedProjects.length) ||
      Boolean(selectedUsers.length)
    )
  }

  const onApplyFilter = () => {
    showFilter.setValue(false)
    isCalendarRanges.setValue(false)
    setQueryState({
      ...queryState,
      page: 1,
      models: (() => {
        const filteredModel = Object.keys(filterModel)
          .filter((key) => key !== "-1")
          .map((key) => filterModel[key])
        return filteredModel.length === 0 ? "" : JSON.stringify(filteredModel)
      })(),
      startDate: startDateCreate ? startDateCreate.toISOString() : undefined,
      endDate: endDateCreate ? endDateCreate.toISOString() : undefined,
      projectIds: selectedProjects.length
        ? JSON.stringify(selectedProjects.map((project) => project.value))
        : undefined,
      userIds: selectedUsers.length
        ? JSON.stringify(selectedUsers.map((user) => user.value))
        : undefined,
    })
  }
  const onClearFilter = () => {
    const modelFilter =
      queryState.models && queryState.models !== ""
        ? JSON.parse(queryState.models).reduce(
            (acc, role) => ({ ...acc, [role]: role }),
            {}
          )
        : { "-1": "-1" }

    setFilterModel(modelFilter)
    showFilter.setValue(false)
    isCalendarRanges.setValue(false)
    if (queryState.startDate) {
      setStartDateCreate(new Date(queryState.startDate))
    } else {
      setStartDateCreate(null)
    }
    if (queryState.endDate) {
      setEndDateCreate(new Date(queryState.endDate))
    } else {
      setEndDateCreate(null)
    }
    if (queryState.projectIds) {
      setSelectedProjects(
        JSON.parse(queryState.projectIds).map((projectId: string) => ({
          label:
            projects.find((project) => project.id === projectId)?.code || "",
          value: projectId,
        }))
      )
    } else {
      setSelectedProjects([])
    }
    if (queryState.userIds) {
      setSelectedUsers(
        JSON.parse(queryState.userIds).map((userId: string) => ({
          label: users.find((user) => user.id === userId)?.email || "",
          value: userId,
        }))
      )
    } else {
      setSelectedUsers([])
    }
  }

  const statusSelects = [
    {
      key: "All",
      value: "-1",
    },
    {
      key: "Projects",
      value: "projects",
    },
    {
      key: "Project components",
      value: "project_components",
    },
    {
      key: "Project builds",
      value: "project_builds",
    },
    {
      key: "Project build components",
      value: "project_build_components",
    },
    {
      key: "Sync commit components",
      value: "sync_commit_components",
    },
    {
      key: "Users",
      value: "users",
    },
  ]

  const handleChangeStatusFilter = (chosenValue: any) => {
    const newChosenValue = cloneDeep(chosenValue)
    setFilterModel((prev) => {
      if (
        Object.keys(newChosenValue).length === 0 ||
        (prev["-1"] !== "-1" && newChosenValue["-1"] === "-1")
      ) {
        return { "-1": "-1" }
      }
      if (
        newChosenValue["-1"] === "-1" &&
        Object.keys(newChosenValue).length > 1
      ) {
        delete newChosenValue["-1"]
      }

      return newChosenValue
    })
  }

  const renderProject = () => {
    return (
      <div className="flex flex-col">
        <p
          className="text-[14px] font-semibold mb-[8px]"
          style={{
            color: isMobile ? "#111111" : "#7A7A7A",
          }}
        >
          Projects
        </p>
        <SelectMulti
          options={projects.map((project) => ({
            label: `${project.code}`,
            value: project.id,
          }))}
          selectedOption={selectedProjects}
          handleChange={(changedData) => {
            setSelectedProjects(changedData || [])
          }}
          noOptionsMessage
          customMenuStyle={
            isMobile
              ? { border: `1px solid #e5e7eb`, background: "#FFFFFF" }
              : {
                  border: `1px solid transparent`,
                  background: "#333333",
                }
          }
          customSingleValueStyle={
            isMobile ? { color: "#000000" } : { color: "#FFFFFF" }
          }
        />
      </div>
    )
  }

  const renderUser = () => {
    return (
      <div className="flex flex-col">
        <p
          className="text-[14px] font-semibold mb-[8px]"
          style={{
            color: isMobile ? "#111111" : "#7A7A7A",
          }}
        >
          Users
        </p>
        <SelectMulti
          options={users.map((user) => ({
            label: user.email,
            value: user.id,
          }))}
          selectedOption={selectedUsers}
          handleChange={(changedData) => {
            setSelectedUsers(changedData || [])
          }}
          noOptionsMessage
          customMenuStyle={
            isMobile
              ? { border: `1px solid #e5e7eb`, background: "#FFFFFF" }
              : {
                  border: `1px solid transparent`,
                  background: "#333333",
                }
          }
          customSingleValueStyle={
            isMobile ? { color: "#000000" } : { color: "#FFFFFF" }
          }
        />
      </div>
    )
  }

  const renderCalendar = () => {
    return (
      <CalendarCustom
        label="Creation period"
        type="date-range-picker"
        styleLabel={{
          color: isMobile ? "#111111" : "#7A7A7A",
          fontSize: 14,
          marginBottom: 8,
        }}
        styleContent={{
          background: isMobile ? "#FFFFFF" : "#333333",
          border: isMobile ? "1px solid #e5e7eb" : "none",
        }}
        visible={isCalendarRanges.value}
        currentDate={[selectionRange]}
        selectedDate={
          startDateCreate && endDateCreate ? (
            <p className="font-normal text-14 leading-24 text-orange-5">
              {`${formatDate(startDateCreate, "DD MMM yyyy")} - ${formatDate(
                endDateCreate,
                "DD MMM yyyy"
              )}`}
            </p>
          ) : null
        }
        onSelectDate={onChangeDate}
        showClear={!!(startDateCreate && endDateCreate)}
        onClear={onClearInputSelectionRanges}
        onCancel={() => {
          onCancelSelectionRanges(startDateCreate, endDateCreate)
        }}
        onApply={onApplySelectionRanges}
        onOpen={onChangeModalSelectRanges(!isCalendarRanges.value)}
      />
    )
  }

  if (isMobile) {
    return (
      <TippyLayout
        visible={showFilter.value}
        headerContent={
          <FilterHeader
            setVisible={showFilter.setValue}
            onCancel={onClearFilter}
            onApply={onApplyFilter}
          />
        }
        mainContent={
          <div>
            <ClearAll
              onClick={() => {
                setFilterModel({ "-1": "-1" })
                setStartDateCreate(null)
                setEndDateCreate(null)
                setSelectedProjects([])
                setSelectedUsers([])
              }}
              containerClass="border-b border-[#E4E4E4]"
              active={onCheckActiveFilter()}
            />

            <div className="flex flex-col">
              <FilterCollapse
                label="Role"
                multiActive
                data={statusSelects.map((el) => ({
                  value: el.value,
                  label: el.key,
                }))}
                chosenValue={filterModel}
                onChange={handleChangeStatusFilter}
              />
              <div className="flex flex-col gap-[24px] px-[24px] mt-[24px]">
                {renderProject()}
                {renderUser()}
              </div>
              <div className="flex flex-col px-[24px]">{renderCalendar()}</div>
            </div>
          </div>
        }
      >
        <FilterIcon
          onClick={() => {
            showFilter.setValue(!showFilter.value)
          }}
          activeIcon={
            queryState.models ||
            queryState.startDate ||
            queryState.endDate ||
            queryState.projectIds ||
            queryState.userIds
              ? true
              : false
          }
        />
      </TippyLayout>
    )
  }
  return (
    <TippyCustomzie
      containerClass="component-card-menu"
      placement="bottom-end"
      interactive
      arrow={false}
      animation="scale"
      offset={[280, 20]}
      allowHTML
      visible={showFilter.value}
      content={
        <div
          className="pt-3.5 pb-3"
          style={{
            background: "#222222",
            borderRadius: 4,
          }}
        >
          <div
            className="flex items-center justify-between mb-3 pl-[16px] select-none"
            style={{
              paddingRight: isMobile ? 0 : 16,
            }}
          >
            <p className="font-semibold text-16 leading-26 text-white">
              Filter
            </p>
            <ClearAll
              onClick={() => {
                setFilterModel({ "-1": "-1" })
                setStartDateCreate(null)
                setEndDateCreate(null)
                setSelectedProjects([])
                setSelectedUsers([])
              }}
              containerClass={isMobile ? "h-[30px]" : undefined}
              active={onCheckActiveFilter()}
            />
          </div>

          <div
            className="flex px-[16px] gap-[8px] overflow-auto max-h-[550px]"
            style={{
              flexDirection: isMobile ? "column" : "row",
            }}
          >
            <div className="flex flex-col w-[275px]">
              <div className="flex flex-col gap-[12px]">
                {renderProject()}
                {renderUser()}
              </div>
              {renderCalendar()}
            </div>
            <div
              style={{
                width: isMobile ? "100%" : 200,
              }}
            >
              <FormSelectFilter
                title="Status"
                type="multi"
                dataSelects={statusSelects}
                styleRoot={{
                  width: "100%",
                }}
                styleKey={{
                  maxWidth: "100%",
                }}
                styleTitle={{
                  marginTop: 0,
                  paddingLeft: isMobile ? 0 : undefined,
                }}
                styleItem={{
                  paddingLeft: isMobile ? 0 : undefined,
                }}
                objActiveSelect={filterModel}
                setObjActiveSelect={handleChangeStatusFilter}
              />
            </div>
          </div>

          <div className="flex justify-end mt-3 mr-3">
            <Button
              title="Apply"
              onClick={onApplyFilter}
              widthBtn={120}
              heightBtn={42}
            />
          </div>
        </div>
      }
      onClickOutside={onClearFilter}
    >
      <FilterIcon
        onClick={() => {
          showFilter.setValue(!showFilter.value)
        }}
        activeIcon={
          queryState.models ||
          queryState.startDate ||
          queryState.endDate ||
          queryState.projectIds ||
          queryState.userIds
            ? true
            : false
        }
      />
    </TippyCustomzie>
  )
}
